import { DataGrid, GridColDef, GridPagination, GridRowsProp, GridToolbarQuickFilter, gridPageCountSelector, useGridApiContext, useGridSelector } from '@mui/x-data-grid';
import { styled } from '@mui/material/styles';
import { Box, Pagination, PaginationItem, TablePaginationProps } from '@mui/material';
import { GreenButton } from '../../assets/style';
import { Result_First_Page_Icon, Result_Last_Page_Icon, Result_Next_Page_Icon, Result_Previous_Page_Icon } from '../../assets/imgs';
import React, { useEffect } from 'react';
import { useGetResultExcelMutation } from '../../features/resultLog/resultLogSlice';

const ExportToExcelAndCustomPaginationContainer = styled(Box)`
  display: flex;  
  width: 100%;
  justify-content: space-between;
  align-items: center;
  @media (max-width: 600px) {
    display: block;
  }
`;

function CustomGridToolbarQuickFilter() {
    return <GridToolbarQuickFilter sx={{
        paddingBottom: '0px',
        borderBottom: '2px solid #E2FFA5',
        '& .MuiInputBase-input': {
            fontSize: 'calc(12px + 0.3vw)',
        }
    }} />
}

function ActionsComponent({ page, onPageChange, className, }: Pick<TablePaginationProps, 'page' | 'onPageChange' | 'className'>) {
    const apiRef = useGridApiContext();
    const pageCount = useGridSelector(apiRef, gridPageCountSelector);

    return (
        <Pagination
            showFirstButton
            showLastButton
            renderItem={(item) => (
                <PaginationItem
                    sx={{
                        fontSize: 'calc(12px + 0.6vw)'
                    }}
                    slots={{ previous: () => <img src={Result_Previous_Page_Icon} />, next: () => <img src={Result_Next_Page_Icon} />, first: () => <img src={Result_First_Page_Icon} />, last: () => <img src={Result_Last_Page_Icon} /> }}
                    {...item}
                />
            )}
            variant="outlined"
            shape="rounded"
            className={className}
            count={pageCount}
            page={page + 1}
            onChange={(event, newPage) => {
                onPageChange(event as any, newPage - 1);
            }}
        />
    );
}

const columns: GridColDef[] = [
    { field: 'customerName', headerName: 'Customer Name', width: 180, minWidth: 50, headerClassName: 'header' },
    { field: 'rfid', headerName: 'QR/RFID/Sample Number', width: 180, minWidth: 50, headerClassName: 'header' },
    { field: 'width', headerName: 'Width (mm)', width: 180, minWidth: 50, headerClassName: 'header' },
    { field: 'length', headerName: 'Length (mm)', width: 180, minWidth: 50, headerClassName: 'header' },
    { field: 'height', headerName: 'Height (mm)', width: 180, minWidth: 50, headerClassName: 'header' },
    { field: 'squareness', headerName: 'Squareness', width: 180, minWidth: 50, headerClassName: 'header' },
    { field: 'massInAir', headerName: 'Mass (Air)', width: 180, minWidth: 50, headerClassName: 'header' },
    { field: 'massInWater', headerName: 'Mass (Water)', width: 180, minWidth: 50, headerClassName: 'header' },
    { field: 'densityByCalculation', headerName: 'Calculation(kg/m3)', width: 180, minWidth: 50, headerClassName: 'header' },
    { field: 'densityByWaterDisplacement', headerName: 'Water-displacement (kg/m3)', width: 180, minWidth: 50, headerClassName: 'header' },
    { field: 'loadAtFailure', headerName: 'Load at Failure(kN)', width: 180, minWidth: 50, headerClassName: 'header' },
    { field: 'typeOfFracture', headerName: 'Type of Fracture', width: 180, minWidth: 50, headerClassName: 'header' },
];

interface ResultDataGridProps {
    testResult: any[]
}

const ResultDataGrid = function (props: ResultDataGridProps) {
    const [selectedRows, setSelectedRows] = React.useState<any[]>([])
    const [listForExcel, setListForExcel] = React.useState<any[]>([])
    const [getResultExcel] = useGetResultExcelMutation();
    let rows: GridRowsProp = props.testResult

    useEffect(() => {
        const newDataList = selectedRows.map((index) => {
            const selectedId = index as number;
            const foundItem = rows.find((item: any) => item.id === selectedId);
            return foundItem;
        });
        setListForExcel(newDataList);
    }, [selectedRows])

    async function downloadExcelFile() {
        try {
            await getResultExcel(listForExcel).then((res) => {
                // console.log((res as any).data)
                const link = document.createElement('a');
                link.href = (res as any).data;
                link.download = 'ResultLogReport.xlsx';
                link.click();
                // URL.revokeObjectURL(url); // Clean up the temporary URL
            });


        } catch (error) {
            // Handle any errors
        }
    };

    function CustomPagination(props: any) {
        return (
            <ExportToExcelAndCustomPaginationContainer>
                <GreenButton variant='contained' sx={{ height: '30px' }} onClick={downloadExcelFile}>Export Excel</GreenButton>
                <GridPagination ActionsComponent={ActionsComponent} {...props} />
            </ExportToExcelAndCustomPaginationContainer>
        );
    }

    return (
        <DataGrid
            initialState={{
                pagination: {
                    paginationModel: {
                        pageSize: 7,
                        /* page: 0 // default value will be used if not passed */
                    },
                },
            }}
            columnHeaderHeight={56}
            getRowHeight={() => 'auto'}
            slots={{
                pagination: CustomPagination,
                toolbar: CustomGridToolbarQuickFilter
            }}
            autoHeight
            getRowClassName={(params) =>
                params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
            }
            disableColumnMenu
            checkboxSelection
            onRowSelectionModelChange={(selectedRows) => setSelectedRows(selectedRows)}
            rows={rows}
            columns={columns}
            sx={{
                '& .MuiDataGrid-selectedRowCount': { display: 'none' },
                '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
                'fontSize': 'calc(12px + 0.3vw)',
                'padding': '0 15px 15px 22px',
                'backgroundColor': 'white',
                '& .header': {
                    fontWeight: '900',
                    paddingTop: '14px',
                    paddingBottom: '14px',
                    paddingLeft: '7px',
                    borderWidth: '4px',
                    borderStyle: 'solid',
                    borderImage: 'linear-gradient(to bottom, rgba(226, 255, 165, 0.6), rgba(67, 182, 86, 0.6)) 1'
                },
                "& .even": {
                    backgroundColor: "#E2E2E2"
                }
            }}
        />
    );
}

export default ResultDataGrid;